import type { SubmitHandler } from 'react-hook-form';
import { Labeled, TextInputField } from '@sixui/ui';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type { IApiDeleteOrganizationResponseBody } from '~/lib/api/modules/Organizations';
import type { IUseDeleteOrganizationMutationValues } from '~/lib/api/modules/Organizations/hooks/useDeleteOrganizationMutation';
import { FormField } from '~/components/atoms/FormField';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { useDeleteOrganizationMutation } from '~/lib/api/modules/Organizations/hooks/useDeleteOrganizationMutation';
import { useOrganizationQuery } from '~/lib/api/modules/Organizations/hooks/useOrganizationQuery';

export type IDeleteOrganizationFormValues = {
  organizationName: string;
};

export type IDeleteOrganizationFormProps = IContainerProps & {
  onSuccess?: (
    outputData: IApiDeleteOrganizationResponseBody,
    inputValues: IUseDeleteOrganizationMutationValues,
  ) => void;
  cancelUrl?: string;
  organizationId: string;
};

export const DeleteOrganizationForm: React.FC<IDeleteOrganizationFormProps> = (
  props,
) => {
  const { sx, onSuccess, cancelUrl, organizationId, ...other } = props;
  const form = useForm<IDeleteOrganizationFormValues>();
  const organizationQuery = useOrganizationQuery({ organizationId });
  const deleteOrganizationMutation = useDeleteOrganizationMutation();
  const { t } = useTranslation();
  const organization = organizationQuery.data;

  const handleSubmit: SubmitHandler<IDeleteOrganizationFormValues> = () => {
    if (deleteOrganizationMutation.isLoading) {
      return;
    }

    return deleteOrganizationMutation.mutateAsync(
      { organizationId },
      { onSuccess },
    );
  };

  return (
    <ContentBlock sx={sx}>
      <ContentBlock.Title
        supportingText={t('DeleteOrganizationForm.supportingText')}
      >
        {t('DeleteOrganizationForm.title')}
      </ContentBlock.Title>

      <ContentBlock.Suspense
        loading={organizationQuery.isLoading}
        error={organizationQuery.error}
      >
        {organization ? (
          <ContentBlock.FormBody
            form={form}
            onSubmit={handleSubmit}
            submitButtonLabel={t('common.delete')}
            cancelUrl={cancelUrl}
            {...other}
          >
            <Labeled
              label={t('DeleteOrganizationForm.fields.confirm.label', {
                organizationName: organization.name,
              })}
              required
            >
              <FormField
                name="organizationName"
                control={form.control}
                as={TextInputField}
                forwardError
                rules={{
                  validate: (value) => {
                    if (value !== organization.name) {
                      return 'Names do not match.';
                    }
                  },
                }}
              />
            </Labeled>
          </ContentBlock.FormBody>
        ) : null}
      </ContentBlock.Suspense>
    </ContentBlock>
  );
};
